import { Link } from "react-router-dom";
import MediaFrame from "@/components/feature/MediaFrame";
import Reveal from "@/components/feature/Reveal";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { clinic } from "@/mocks/clinic";
import type { SlimZeroDetailPage } from "@/mocks/slimZeroDetails";

/**
 * Medical Slim ZERO 配下（RF / 脂肪冷却 / 医療用EMS）共通のHERO。
 * FV画像は正式ビジュアルを表示する。
 */
export default function SlimZeroTreatmentHero({ data }: { data: SlimZeroDetailPage }) {
  return (
    <section className="relative overflow-hidden border-b border-background-300/70 bg-background-50 pb-16 pt-28 md:pb-20 md:pt-36 lg:pb-24 lg:pt-40">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 top-24 h-72 w-72 rounded-full bg-secondary-100/50 blur-3xl"
      />

      <div className="u-container relative">
        {/* 施術詳細：HOME ＞ 親カテゴリ ＞ 現在の施術（現在ページは非リンク） */}
        <Breadcrumb
          items={[
            { label: "HOME", href: "/", path: "/" },
            { label: "医療痩身", href: "/treatment/slim-zero", path: "/treatment/slim-zero" },
            { label: data.jp, path: `/slim/${data.slug}` },
          ]}
        />

        <div className="mt-10 grid grid-cols-1 gap-12 lg:mt-14 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-16">
          <div>
            <Reveal>
              <span className="en-eyebrow block text-[11px] text-secondary-700 md:text-[12px]">
                MEDICAL SLIM ZERO
              </span>
              <span className="mt-5 block h-px w-12 bg-accent-500" />
              <h1 className="mt-7 font-heading text-[30px] leading-[1.5] tracking-[0.02em] text-foreground-950 md:text-[40px]">
                {data.jp}
              </h1>
              <p className="mt-7 font-heading text-[19px] leading-[1.9] text-foreground-900 md:text-[23px]">
                {data.heroMain}
              </p>
              <p className="mt-6 text-[14px] leading-[2.2] text-foreground-700 md:text-[15px]">
                {data.heroSub}
              </p>
            </Reveal>

            <Reveal delay={140}>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12px] text-foreground-600">
                <Link
                  to="/treatment/slim-zero"
                  className="group inline-flex items-center gap-2 whitespace-nowrap text-foreground-800 transition-colors hover:text-accent-700"
                >
                  <i className="ri-arrow-left-line transition-transform group-hover:-translate-x-0.5" aria-hidden />
                  Medical Slim ZEROへ戻る
                </Link>
                <span className="hidden h-4 w-px bg-foreground-950/15 sm:block" />
                <span className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-accent-700" aria-hidden />
                  渋谷駅近く
                </span>
                <span className="hidden h-4 w-px bg-foreground-950/15 sm:block" />
                <span className="flex items-center gap-2">
                  <i className="ri-time-line text-accent-700" aria-hidden />
                  診療時間 {clinic.hours}
                </span>
              </div>
            </Reveal>
          </div>

          <Reveal delay={120}>
            <MediaFrame
              src={data.heroImage}
              alt={data.heroImageAlt}
              tone="greige"
              objectPosition={data.heroObjectPosition}
              className="aspect-[3/2] w-full"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}